import { Select, TableColumnsType } from "antd";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getRetiredStudentByMonth } from "../../../api/statistics.api";
import useAxios from "../../../hooks/useAxios";
import useNotification from "../../../hooks/useNotification";
import exportTable from "../../../lib/exportTable";
import { IRetiredStudentByMonth, IRetiredStudentByMonthParams } from "../../../models/statistics.model";
import { RootState } from "../../../store/rootReducer";
import Button from "../../Button";
import DownloadButton from "../../DownloadButton";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";
import MultiCampusModal from "../regular-test/modals/MultiCampusModal";
import moment from "moment";
import { getReverseYears } from "../../../lib/util";
import TableEmpty from "../../common/TableEmpty";
import SelectCampusMultiChoice from "../../SelectCampusMultiChoice";

const TableContainer = () => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0005 = 연도
  const SY0005 = commonCodes.find(item => item.commonCode === 'SY0005')?.detailCode;
  // PR0002 = 학기유형
  const PR0002 = commonCodes.find((item) => item.commonCode === 'PR0002')?.detailCode;
  const { infoNotification } = useNotification();
  const { arr: res, isLoading, error, executeArr: execute } = useAxios<any>();
  
  const [data, setData] = useState<any>([]);
  const [learningYearCode, setLearningYearCode] = useState<string>();
  const [semesterType, setSemesterType] = useState("RG");
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);

  useEffect(() => {
    defSelectBoxSetting();
  }, [])

  const defSelectBoxSetting = () => {
    //검색 필드 Default값 셋팅
    setLearningYearCode(moment().format('YYYY'));
  }

  useEffect(() => {
    if (res.length && !isLoading) {
      let _data: any = [];
      // @ts-ignore
      res.at(0).data.forEach(campus => {
        campus.monthlyCountInfo.forEach((month: any) => {
          if (month.month) {
            const item: any = {
              campusCode: campus.campusCode,
              campusEngName: campus.campusEngName,
              campusKorName: campus.campusKorName,
              month: Number(month.month),
              retireStudentCount: month.retireStudentCount,
            }

            // @ts-ignore
            const prevYearData = res.at(1)?.data?.find((prevCampus: any) => prevCampus.campusCode === campus.campusCode)?.monthlyCountInfo?.find((prevMonth: any) => prevMonth.month?.trim() === month.month?.trim());
            if (prevYearData) {
              item.prevRetireStudentCount = prevYearData.retireStudentCount;
              item.diff = item.retireStudentCount - item.prevRetireStudentCount;
              item.diffPercent = Math.round(item.diff / item.prevRetireStudentCount * 100);
            } else {
              item.prevRetireStudentCount = null;
              item.diff = null;
              item.diffPercent = null;
            }

            _data.push(item);
          }
        });
      });

      if (auth.hqYn === 'Y') {
        _data = calculateTotal(_data);
      }
      setData(_data);
    }
  }, [res, isLoading]);

  const calculateTotal = (_data: any) => {
    // @ts-ignore
    const months: number[] = _.uniqBy(_data, 'month').map(item => item.month);
    months.forEach(month => {
      const total: any = {
        campusCode: 'total',
        campusEngName: 'Total',
        campusKorName: t('statistics.studentRetire.column1'),
        month: month,
        retireStudentCount: _.sumBy(_data.filter((item: any) => item.month === month), 'retireStudentCount'),
        prevRetireStudentCount: _.sumBy(_data.filter((item: any) => item.month === month), 'prevRetireStudentCount'),
      };

      total.diff = total.retireStudentCount - total.prevRetireStudentCount;
      total.diffPercent = total.prevRetireStudentCount ? Math.round(total.diff / total.prevRetireStudentCount * 100) : null;

      _data.push(total);
    });

    return _data;
  }

  const onSearch = () => {
    if (!learningYearCode) {
      return infoNotification(t('student.student.message30'));
    }

    const req: IRetiredStudentByMonthParams = {
      learningYearCode,
      campusCode: auth.hqYn === 'Y'
        ? campusCodes
        : (campusCodes.length ? campusCodes : auth.campus),
    };
    const prevYearCode = String(Number(learningYearCode) - 1);
    if (semesterType) {
      req.semesterType = semesterType;
    }

    execute([
      getRetiredStudentByMonth(req),
      getRetiredStudentByMonth({ ...req, learningYearCode: prevYearCode }),
    ]);
  }

  const onExportTable = () => {
    const _data: any = _.cloneDeep(data);
    _data.forEach((item: any) => {
      item.prevRetireStudentCount = item.prevRetireStudentCount || 0;
      item.diff = item.diff || '-';
      item.diffPercent = item.diffPercent || '-';
    });
    const currentDate = moment(new Date());
    exportTable(columns, _data, `${t('statistics.studentRetire.2')}_(${currentDate.format('YYYYMMDD')})`, t('statistics.studentRetire.3'), [12, 10, 15, 15, 15, 15]);
  }

  const columns: TableColumnsType<IRetiredStudentByMonth> = [
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusKorName',
      key: 'campusKorName',
      align: 'center',
    },
    {
      title: t('student.student.column48'),
      dataIndex: 'month',
      key: 'month',
      align: 'center',
    },
    {
      title: `(${learningYearCode}) ${t('statistics.studentRetire.column2')}`,
      dataIndex: 'retireStudentCount',
      key: 'retireStudentCount',
      align: 'center',
    },
    {
      title: `(${Number(learningYearCode) - 1}) ${t('statistics.studentRetire.column2')}`,
      dataIndex: 'prevRetireStudentCount',
      key: 'prevRetireStudentCount',
      align: 'center',
    },
    {
      title: t('statistics.studentRetire.column3'),
      dataIndex: 'diff',
      key: 'diff',
      align: 'center',
    },
    {
      title: t('statistics.studentRetire.column4'),
      dataIndex: 'diffPercent',
      key: 'diffPercent',
      align: 'center',
    },
  ];

  return (
    <>
      {isLoading && <Loader />}
      <ErrorResponse error={error} />

      <div className="common-search">
        <div className="search-area">
          <ul className="search-bx">
            <li>
              <label>{t('common.menu.campus')}</label> {/* 캠퍼스 */}
              <SelectCampusMultiChoice
                placeholder={t('statistics.eLearning.title2')}
                onChange={setCampusCodes}
                value={campusCodes}
              />
              <label>
                ({campusCodes.length ? `${campusCodes.length}${t('statistics.attendance.text1')}` : t('common.text.all')})
              </label>
            </li>
            <li>
              <label>{t('statistics.refund.placeholder1')}</label> {/* 연도 */}
              <div>
                <Select                  
                  placeholder={t('statistics.regularTest.placeholder2')}
                  value={learningYearCode}
                  onChange={value => setLearningYearCode(value)}
                >
                  {getReverseYears(SY0005).map(item => (
                    <Select.Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {item.korName}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </li>
            <li>
              <label>
                {t('program.semester.column11')}
              </label>
              <Select
                className="w-120p"
                value={semesterType}
                onChange={value => setSemesterType(value)}
              >
                {
                  PR0002?.map(({korName: label, detailCode: value}) => {
                    return (
                      <Select.Option key={value} value={value}>
                        {label}
                      </Select.Option>
                    )
                  })
                }
              </Select>
              <div className="comm-btn__area">
                <Button
                  className="btn-basic small black"
                  onClick={onSearch}
                  text={t('common.button.search')}
                />
              </div>
            </li>
          </ul>
        </div>
      </div>
      
      {
        data.length
          ? (
            <>
            <div className="comm-bx__between">
              <div className="comm-sch_result ml-auto">
              🏷️ 퇴원생 산정 기준은 ‘학생 퇴원처리일자’입니다. 
              </div>
            </div>
            <div className="comm-table__area line-type">
              <table>
                <thead>
                  <tr>
                    <th rowSpan={2}>{t('common.menu.campus')}</th>
                    <th rowSpan={2}>{t('student.student.column48')}</th>
                    <th>{learningYearCode}</th>
                    <th>{Number(learningYearCode) - 1}</th>
                    <th colSpan={2}>{t('statistics.studentEnrollMonth.column7')}</th>
                  </tr>
                  <tr>
                    <th>{t('statistics.studentRetire.column2')}</th>
                    <th>{t('statistics.studentRetire.column2')}</th>
                    <th>{t('statistics.studentRetire.column5')}</th>
                    <th>{t('statistics.studentEnroll.column2')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item: any, index: number) => (
                    <tr key={index} className={item.campusCode === 'total' ? 'tfoot' : ''}>
                      <td>{item.campusKorName}</td>
                      <td>{item.month}</td>
                      <td>{item.retireStudentCount}</td>
                      <td>{item.prevRetireStudentCount || 0}</td>
                      <td>{item.diff || '-'}</td>
                      <td>{item.diffPercent ? `${item.diffPercent}%` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="comm-bx__space">
                <DownloadButton onClick={onExportTable} />
              </div>
            </div>
            </>
            )
          : isLoading
            ? null
            : (
              <TableEmpty text={t('statistics.appday.text1')} />
              )
      }
      {multiCampusVisible &&
        <MultiCampusModal
          visible={multiCampusVisible}
          setVisible={setMultiCampusVisible}
          campusCodes={campusCodes}
          setCampusCodes={setCampusCodes}
        />
      }
    </>
  );
}

export default TableContainer;
